import { Component, OnInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { FormBuilder, FormGroup } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { SelectionModel } from '@angular/cdk/collections';
import { AdministrativeIndicationsReportService } from '../../../../services/administrative-indications-report-v2.service';
import { InvoiceService } from '../../../../services/invoice.service';
import { DoctorV2Service, ListDoctorsResponse } from '../../../../services/doctor-v2.service';
import { AttachReportModalComponent } from './modals/attach-report/attach-report.modal';
import { EIndicationStatus } from 'src/app/_pages/private/indication/enums/indication.enum';

@Component({
  selector: 'app-administrative-indications',
  templateUrl: './indications-report.component.html',
  styleUrls: ['./indications-report.component.scss'],
})
export class AdministrativeIndicationsComponent implements OnInit {
  loading = false;
  exporting = false;
  emitting = false;
  filtersApplied = false;
  filtersVisible = false;

  doctors: Array<{ id: number; name: string }> = [];

  readonly indicationStatuses: Array<{ label: string; value: EIndicationStatus | null }> = [
    { label: 'Todos', value: null },
    ...Object.values(EIndicationStatus).map((status) => ({ label: status, value: status })),
  ];

  readonly hasDocumentOptions: Array<{ label: string; value: boolean | null }> = [
    { label: 'Todos', value: null },
    { label: 'Com laudo', value: true },
    { label: 'Sem laudo', value: false },
  ];

  readonly hasFilesAttachmentOptions: Array<{ label: string; value: boolean | null }> = [
    { label: 'Todos', value: null },
    { label: 'Com Anexo', value: true },
    { label: 'Sem Anexo', value: false },
  ];

  readonly invoiceStatusOptions: Array<{ label: string; value: 'sem_nota' | 'erro' | null }> = [
    { label: 'Todos', value: null },
    { label: 'Sem nota', value: 'sem_nota' },
    { label: 'Com erro', value: 'erro' },
  ];

  selection = new SelectionModel<any>(true, []);

  dataSource: MatTableDataSource<any> = new MatTableDataSource([]);
  displayedColumns: string[] = [
    'select',
    'indicationId',
    'hasFilesAttachment',
    'patientName',
    'patientCpf',
    'patientMobile',
    'scheduleDate',
    'doctorName',
    'indicationStatus',
    'scheduleStatus',
    'benefit',
    'hasDocument',
    'accountName',
    'accountEmail',
    'userEmail',
    'nfeId',
    'nfeStatus',
    'actions',
  ];

  total = 0;
  pageIndex = 0;
  pageSize = 10;

  filters: FormGroup;

  constructor(
    private readonly service: AdministrativeIndicationsReportService,
    private readonly invoiceService: InvoiceService,
    private readonly fb: FormBuilder,
    private readonly dialog: MatDialog,
    private readonly doctorService: DoctorV2Service
  ) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 6);

    this.filters = this.fb.group({
      search: [''],
      dateFrom: [sevenDaysAgo],
      dateTo: [today],
      status: [null],
      hasDocument: [null],
      hasFilesAttachment: [null],
      doctorId: [null],
      invoiceStatus: [null],
    });
  }

  ngOnInit(): void {
    this.doctorService.findAll().subscribe((list: ListDoctorsResponse[]) => {
      this.doctors = (list || []).map((d) => ({ id: d.id, name: d.name }));
    });

    this.filters.valueChanges.subscribe((v) => {
      this.filtersApplied =
        !!v.status ||
        (v.hasDocument !== null && v.hasDocument !== undefined) ||
        (v.doctorId !== null && v.doctorId !== undefined) ||
        (v.hasFilesAttachment !== null && v.hasFilesAttachment !== undefined) ||
        (v.invoiceStatus !== null && v.invoiceStatus !== undefined);
    });

    this.onSearch();
  }

  get maxDateTo(): Date | null {
    const df: Date | null = this.filters.value.dateFrom;
    if (!df) return null;
    const max = new Date(df);
    max.setDate(max.getDate() + 29);
    return max;
  }

  get rangeError(): boolean {
    const df: Date | null = this.filters.value.dateFrom;
    const dt: Date | null = this.filters.value.dateTo;
    if (!df || !dt) return false;
    return dt.getTime() - df.getTime() > 30 * 24 * 60 * 60 * 1000;
  }

  get searchActive(): boolean {
    return !!(this.filters.value.search || '').trim();
  }

  get hasPeriodFilter(): boolean {
    return this.searchActive || !!(this.filters.value.dateFrom && this.filters.value.dateTo);
  }

  toggleFilters(): void {
    this.filtersVisible = !this.filtersVisible;
  }

  applyFilters(): void {
    const v = this.filters.value;
    this.filtersApplied =
      !!v.status ||
      (v.hasDocument !== null && v.hasDocument !== undefined) ||
      (v.doctorId !== null && v.doctorId !== undefined) ||
      (v.hasFilesAttachment !== null && v.hasFilesAttachment !== undefined) ||
      (v.invoiceStatus !== null && v.invoiceStatus !== undefined);
    this.onSearch();
  }

  clearFilters(): void {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 6);
    this.filters.patchValue({
      dateFrom: sevenDaysAgo,
      dateTo: today,
      status: null,
      hasDocument: null,
      hasFilesAttachment: null,
      doctorId: null,
      invoiceStatus: null,
    });
    this.filtersApplied = false;
    this.onSearch();
  }

  onSearch(event?: PageEvent): void {
    if (!this.hasPeriodFilter) return;

    this.loading = true;
    this.selection.clear();

    if (event) {
      this.pageIndex = event.pageIndex;
      this.pageSize = event.pageSize;
    } else {
      this.pageIndex = 0;
    }

    const v = this.filters.value;
    const baseFilter: any = {};

    if (this.searchActive) {
      baseFilter.q = v.search.trim();
    } else {
      if (v.status) {
        baseFilter.status = v.status;
      }
      if (v.hasDocument !== null && v.hasDocument !== undefined) {
        baseFilter.hasDocument = v.hasDocument;
      }
      if (v.hasFilesAttachment !== null && v.hasFilesAttachment !== undefined) {
        baseFilter.hasFilesAttachment = v.hasFilesAttachment;
      }
      if (v.doctorId) {
        baseFilter.doctorId = v.doctorId;
      }
      if (v.dateFrom && v.dateTo) {
        baseFilter.schedules = {
          date: [
            this.formatDateToYMD(v.dateFrom) + 'T00:00:00',
            this.formatDateToYMD(v.dateTo) + 'T23:59:59',
          ],
        };
      }
      if (v.invoiceStatus === 'sem_nota') {
        baseFilter.invoice = { id: null };
      } else if (v.invoiceStatus === 'erro') {
        baseFilter.invoice = { statusNf: 'erro' };
      }
    }

    this.service
      .search({
        filter: baseFilter,
        page: this.pageIndex + 1,
        perPage: this.pageSize,
      })
      .subscribe(
        (datatable) => {
          this.dataSource.data = [...(datatable.rows || [])];
          this.total = datatable.total || 0;
          this.loading = false;
        },
        () => {
          this.loading = false;
        }
      );
  }

  exportCsv(): void {
    this.exporting = true;

    const v = this.filters.value;
    const filter: any = {};

    if (this.searchActive) {
      filter.q = v.search.trim();
    } else {
      if (v.status) {
        filter.status = v.status;
      }
      if (v.hasDocument !== null && v.hasDocument !== undefined) {
        filter.hasDocument = v.hasDocument;
      }
      if (v.hasFilesAttachment !== null && v.hasFilesAttachment !== undefined) {
        filter.hasFilesAttachment = v.hasFilesAttachment;
      }
      if (v.doctorId) {
        filter.doctorId = v.doctorId;
      }
      if (v.dateFrom && v.dateTo) {
        filter.schedules = {
          date: [
            this.formatDateToYMD(v.dateFrom) + 'T00:00:00',
            this.formatDateToYMD(v.dateTo) + 'T23:59:59',
          ],
        };
      }
      if (v.invoiceStatus === 'sem_nota') {
        filter.invoice = { id: null };
      } else if (v.invoiceStatus === 'erro') {
        filter.invoice = { statusNf: 'erro' };
      }
    }

    this.service.exportCsv(filter).subscribe(
      (response) => {
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = 'Relatorio-Indicacoes.csv';

        if (contentDisposition) {
          const match = contentDisposition.match(/filename="(.+)"/);
          if (match && match[1]) {
            filename = match[1];
          }
        }

        const blob = new Blob([response.body], {
          type: 'text/csv;charset=utf-8;',
        });
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();

        window.URL.revokeObjectURL(url);
        this.exporting = false;
      },
      () => {
        this.exporting = false;
      }
    );
  }

  applySearch(): void {
    this.onSearch();
  }

  clearSearch(): void {
    this.filters.patchValue({ search: '' });
    this.onSearch();
  }

  isAttachable(row: any): boolean {
    return String(row?.status).toLowerCase().includes('atendido');
  }

  openAttachReport(row: any): void {
    const ref = this.dialog.open(AttachReportModalComponent, {
      width: '750px',
      data: { row: row },
      disableClose: true,
    });
    ref.afterClosed().subscribe((ok: boolean) => {
      if (ok) this.onSearch();
    });
  }

  isAllSelected(): boolean {
    return (
      this.dataSource.data.length > 0 &&
      this.selection.selected.length === this.dataSource.data.length
    );
  }

  isIndeterminate(): boolean {
    const s = this.selection.selected.length;
    return s > 0 && s < this.dataSource.data.length;
  }

  toggleAllRows(checked: boolean): void {
    if (checked) {
      this.dataSource.data.forEach((r) => this.selection.select(r));
    } else {
      this.selection.clear();
    }
  }

  emitSelectedInvoices(): void {
    const ids = this.selection.selected.map((r) => r.id);
    if (!ids.length) return;
    this.emitting = true;
    this.invoiceService.emitByIds(ids).subscribe(
      () => {
        this.emitting = false;
        this.selection.clear();
        this.onSearch();
      },
      () => {
        this.emitting = false;
      }
    );
  }

  formatDateToYMD(date: Date): string {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  formatPhone(phone: string | null | undefined): string {
    if (!phone) return '-';
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 11)
      return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
    if (digits.length === 10)
      return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
    return phone;
  }
}
